import React from 'react';
import {Link} from 'react-router-dom';
import styles from './PublicInfoPage.module.css';

const PUBLIC_CONTACT_EMAIL = 'support@xpsr.app';
const XPSR_LOGO_SRC = `${process.env.PUBLIC_URL}/xpsr-logo.png`;

interface PublicDocumentSection {
  title: string;
  body: React.ReactNode;
}

interface PublicDocumentPageProps {
  title: string;
  meta: string;
  highlight: string;
  sections: PublicDocumentSection[];
  footerLinks: Array<{to: string; label: string}>;
}

const PublicDocumentPage: React.FC<PublicDocumentPageProps> = ({
  title,
  meta,
  highlight,
  sections,
  footerLinks,
}) => (
  <div className={styles.pageRoot}>
    <header className={styles.header}>
      <Link to="/" className={styles.logo}>
        <img
          src={XPSR_LOGO_SRC}
          alt=""
          className={styles.logoImage}
          width="44"
          height="44"
          aria-hidden="true"
        />
        <span className={styles.logoText}>XPSR</span>
      </Link>
    </header>

    <main className={styles.container}>
      <h1>{title}</h1>
      <p className={styles.meta}>{meta}</p>

      <div className={styles.highlightBox}>{highlight}</div>

      {sections.map((section) => (
        <section key={section.title}>
          <h2>{section.title}</h2>
          <div className={styles.sectionBody}>{section.body}</div>
        </section>
      ))}
    </main>

    <footer className={styles.footer}>
      <nav className={styles.footerLinks} aria-label="Public pages">
        {footerLinks.map((link) => (
          <Link to={link.to} key={link.to}>
            {link.label}
          </Link>
        ))}
      </nav>
      <p>&copy; 2026 XPSR (Xposure). All rights reserved.</p>
    </footer>
  </div>
);

export const AboutPage: React.FC = () => (
  <PublicDocumentPage
    title="About XPSR"
    meta="Public platform information"
    highlight="XPSR is a social content and talent platform built for users to showcase profiles, media, services, and professional connections."
    sections={[
      {
        title: 'What is XPSR?',
        body: (
          <p>
            XPSR helps athletes, artists, creators, professionals, and
            organizations present their work, publish content, offer services,
            and connect with other users through a secure account experience.
          </p>
        ),
      },
      {
        title: 'How We Use Email',
        body: (
          <p>
            XPSR sends transactional verification codes only when users request
            account access or start a sign-in flow. These messages are used for
            authentication and account security.
          </p>
        ),
      },
      {
        title: 'What We Do Not Send',
        body: (
          <p>
            We do not send marketing emails, newsletters, purchased-list
            emails, scraped-list emails, or unsolicited bulk messages through
            our account access email system.
          </p>
        ),
      },
      {
        title: 'Contact',
        body: (
          <p>
            If you have questions about XPSR or received an email you did not
            request, contact us at{' '}
            <a href={`mailto:${PUBLIC_CONTACT_EMAIL}`}>{PUBLIC_CONTACT_EMAIL}</a>.
          </p>
        ),
      },
    ]}
    footerLinks={[
      {to: '/privacy', label: 'Privacy Policy'},
      {to: '/terms', label: 'Terms of Service'},
      {to: '/contact', label: 'Contact Us'},
    ]}
  />
);

export const ContactPage: React.FC = () => (
  <PublicDocumentPage
    title="Contact Us"
    meta="Support and contact information"
    highlight="Use this page to reach XPSR for account access, product questions, billing, general support, or emails received in error."
    sections={[
      {
        title: 'Contact email',
        body: (
          <p>
            Send account access, product, billing, and general support
            questions to{' '}
            <a href={`mailto:${PUBLIC_CONTACT_EMAIL}`}>{PUBLIC_CONTACT_EMAIL}</a>.
          </p>
        ),
      },
      {
        title: 'Emails received in error',
        body: (
          <p>
            If you received a verification email you did not request, include
            the email address and time received so we can review it.
          </p>
        ),
      },
      {
        title: 'Platform or safety concerns',
        body: (
          <p>
            You can also use this address for impersonation, spam, unsafe
            content, suspicious activity, unauthorized email, or platform misuse
            reports.
          </p>
        ),
      },
    ]}
    footerLinks={[
      {to: '/about', label: 'About'},
      {to: '/privacy', label: 'Privacy Policy'},
      {to: '/terms', label: 'Terms of Service'},
    ]}
  />
);

export const AccountDeletionPage: React.FC = () => (
  <PublicDocumentPage
    title="Account Deletion"
    meta="How to delete your XPSR account and associated data"
    highlight="XPSR account holders can delete their account directly from the mobile app in Settings. This page also provides a web-based deletion request path for users who cannot access the app."
    sections={[
      {
        title: 'Delete in the app',
        body: (
          <p>
            Open XPSR, go to <strong>Settings</strong>, then choose{' '}
            <strong>Delete Account</strong>. This permanently removes your
            account, profile, posts, messages, and related app data from the
            XPSR platform.
          </p>
        ),
      },
      {
        title: 'Delete without app access',
        body: (
          <p>
            If you cannot sign in to the app, send a deletion request to{' '}
            <a href={`mailto:${PUBLIC_CONTACT_EMAIL}`}>{PUBLIC_CONTACT_EMAIL}</a>{' '}
            from the email address linked to your XPSR account. Include
            {' '} "Account Deletion Request" in the subject line.
          </p>
        ),
      },
      {
        title: 'Subscriptions',
        body: (
          <p>
            Deleting your XPSR account does not automatically cancel a Google
            Play subscription. Subscription billing is managed separately in
            Google Play and should be canceled there if needed.
          </p>
        ),
      },
    ]}
    footerLinks={[
      {to: '/privacy', label: 'Privacy Policy'},
      {to: '/terms', label: 'Terms of Service'},
      {to: '/contact', label: 'Contact Us'},
    ]}
  />
);

export const ChildSafetyStandardsPage: React.FC = () => (
  <PublicDocumentPage
    title="Child Safety Standards"
    meta="XPSR child safety and CSAE prevention standards"
    highlight="XPSR prohibits child sexual abuse and exploitation, grooming, sexualization of minors, and any content or behavior that endangers children."
    sections={[
      {
        title: 'Zero-tolerance policy',
        body: (
          <p>
            XPSR does not allow child sexual abuse and exploitation material,
            child sexualization, grooming, sextortion, trafficking, or any use
            of the platform to harm minors. Accounts or content that violate
            these rules may be removed immediately and reported where required.
          </p>
        ),
      },
      {
        title: 'Detection and enforcement',
        body: (
          <p>
            We review user reports, investigate credible safety complaints,
            restrict or remove violating content, suspend offending accounts,
            and preserve relevant information when required for safety,
            compliance, or legal obligations.
          </p>
        ),
      },
      {
        title: 'How to report concerns',
        body: (
          <p>
            Users can contact XPSR about child safety or exploitation concerns
            at{' '}
            <a href={`mailto:${PUBLIC_CONTACT_EMAIL}`}>{PUBLIC_CONTACT_EMAIL}</a>.
            Reports should include the account, content, or incident details so
            the issue can be investigated quickly.
          </p>
        ),
      },
      {
        title: 'Escalation and compliance',
        body: (
          <p>
            XPSR complies with applicable child safety laws and will escalate
            actionable child sexual abuse and exploitation matters to the
            appropriate regional or national authorities when required.
          </p>
        ),
      },
    ]}
    footerLinks={[
      {to: '/privacy', label: 'Privacy Policy'},
      {to: '/terms', label: 'Terms of Service'},
      {to: '/contact', label: 'Contact Us'},
    ]}
  />
);

export default PublicDocumentPage;
